import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { CarouselShell } from '@/components/carousel/CarouselShell';
import {
  computePrizeStats,
  createCarouselPrize,
  deleteCarouselPrize,
  listCarouselPrizes,
  listCarouselWins,
  mapSpinError,
  updateCarouselPrize,
  uploadPrizeImage,
  type CarouselPrize,
  type CarouselWinWithPrize,
} from '@/lib/carousel-service';
import {
  ArrowLeft,
  Gift,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

type PrizeFormState = {
  name: string;
  total_quantity: string;
  is_big_prize: boolean;
  file: File | null;
  previewUrl: string | null;
};

const emptyForm = (): PrizeFormState => ({
  name: '',
  total_quantity: '1',
  is_big_prize: false,
  file: null,
  previewUrl: null,
});

export default function CarouselAdmin() {
  const { companyId, userRole } = useAuth();
  const { toast } = useToast();
  const isAdmin = userRole === 'admin';

  const [prizes, setPrizes] = useState<CarouselPrize[]>([]);
  const [wins, setWins] = useState<CarouselWinWithPrize[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CarouselPrize | null>(null);
  const [form, setForm] = useState<PrizeFormState>(emptyForm());
  const [winSearch, setWinSearch] = useState('');

  const stats = useMemo(() => computePrizeStats(prizes), [prizes]);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [prizeRows, winRows] = await Promise.all([
        listCarouselPrizes(companyId),
        listCarouselWins(companyId),
      ]);
      setPrizes(prizeRows);
      setWins(winRows);
    } catch (error) {
      toast({
        title: 'Veriler yüklenemedi',
        description: mapSpinError(error),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [companyId, toast]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (prize: CarouselPrize) => {
    setEditing(prize);
    setForm({
      name: prize.name,
      total_quantity: String(prize.total_quantity),
      is_big_prize: prize.is_big_prize,
      file: null,
      previewUrl: prize.image_url,
    });
    setDialogOpen(true);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;
    setForm((prev) => ({
      ...prev,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    event.target.value = '';
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!companyId) return;

    const quantity = Number.parseInt(form.total_quantity, 10);
    if (!form.name.trim() || !Number.isFinite(quantity) || quantity < 0) {
      toast({
        title: 'Eksik bilgi',
        description: 'Geçerli bir ödül adı ve adet girin.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      let imageUrl = editing?.image_url ?? null;
      let imagePath = editing?.image_path ?? null;

      if (form.file) {
        const upload = await uploadPrizeImage(companyId, form.file);
        if (!upload.success) {
          throw new Error(upload.error || 'Görsel yüklenemedi');
        }
        imageUrl = upload.url;
        imagePath = upload.path;
      }

      if (editing) {
        await updateCarouselPrize(editing.id, {
          name: form.name,
          is_big_prize: form.is_big_prize,
          image_url: imageUrl,
          image_path: imagePath,
          total_quantity: quantity,
          current: {
            total_quantity: editing.total_quantity,
            remaining_quantity: editing.remaining_quantity,
          },
        });
        toast({ title: 'Ödül güncellendi' });
      } else {
        await createCarouselPrize(companyId, {
          name: form.name,
          total_quantity: quantity,
          is_big_prize: form.is_big_prize,
          image_url: imageUrl,
          image_path: imagePath,
        });
        toast({ title: 'Ödül eklendi' });
      }

      setDialogOpen(false);
      await loadData();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: mapSpinError(error),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (prize: CarouselPrize) => {
    try {
      await updateCarouselPrize(prize.id, { is_active: !prize.is_active });
      await loadData();
    } catch (error) {
      toast({
        title: 'Güncellenemedi',
        description: mapSpinError(error),
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (prize: CarouselPrize) => {
    const given = prize.total_quantity - prize.remaining_quantity;
    if (given > 0) {
      toast({
        title: 'Silinemez',
        description: 'Bu ödülden kazanım kaydı var. Pasif hale getirebilirsiniz.',
        variant: 'destructive',
      });
      return;
    }
    if (!window.confirm(`"${prize.name}" ödülünü silmek istiyor musunuz?`)) return;

    try {
      await deleteCarouselPrize(prize.id, prize.image_path);
      toast({ title: 'Ödül silindi' });
      await loadData();
    } catch (error) {
      toast({
        title: 'Silinemedi',
        description: mapSpinError(error),
        variant: 'destructive',
      });
    }
  };

  const filteredWins = useMemo(() => {
    const q = winSearch.trim().toLocaleLowerCase('tr');
    if (!q) return wins;
    return wins.filter((win) => {
      const fullName = `${win.customer_first_name} ${win.customer_last_name}`.toLocaleLowerCase('tr');
      const prizeName = (win.prize?.name ?? '').toLocaleLowerCase('tr');
      const staffName = (win.staff?.full_name ?? '').toLocaleLowerCase('tr');
      return fullName.includes(q) || prizeName.includes(q) || staffName.includes(q);
    });
  }, [wins, winSearch]);

  if (!isAdmin) {
    return (
      <CarouselShell>
        <div className="p-6 max-w-xl mx-auto text-center space-y-4">
          <h1 className="text-2xl font-bold">Erişim Yok</h1>
          <p className="text-muted-foreground">
            Ödül yönetimi yalnızca şirket yöneticileri içindir.
          </p>
          <Button asChild variant="outline">
            <Link to="/carousel">Çekiliş sayfasına dön</Link>
          </Button>
        </div>
      </CarouselShell>
    );
  }

  return (
    <CarouselShell>
      <div className="p-4 md:p-6 space-y-6 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/carousel" className="inline-flex items-center gap-1 hover:text-foreground">
                <ArrowLeft className="h-4 w-4" />
                Çekiliş
              </Link>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Gift className="h-7 w-7" />
              Ödül Yönetimi
            </h1>
            <p className="text-sm text-muted-foreground">
              Kalan {stats.remaining} · Verilen {stats.given} · Büyük ödül kalan {stats.bigRemaining}
            </p>
          </div>
          <Button onClick={openCreate} className="rounded-xl">
            <Plus className="h-4 w-4 mr-2" />
            Ödül Ekle
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {prizes.map((prize) => {
                const given = prize.total_quantity - prize.remaining_quantity;
                return (
                  <Card key={prize.id} className={!prize.is_active ? 'opacity-60' : undefined}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg leading-tight">{prize.name}</CardTitle>
                        {prize.is_big_prize && (
                          <Badge className="bg-amber-500/15 text-amber-700 border-amber-300 shrink-0">
                            <Sparkles className="h-3 w-3 mr-1" />
                            Büyük
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="aspect-square rounded-xl overflow-hidden bg-muted">
                        {prize.image_url ? (
                          <img
                            src={prize.image_url}
                            alt={prize.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center text-muted-foreground">
                            <Gift className="h-12 w-12" />
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-sm">
                        <div>
                          <div className="font-bold tabular-nums">{prize.total_quantity}</div>
                          <div className="text-xs text-muted-foreground">Toplam</div>
                        </div>
                        <div>
                          <div className="font-bold tabular-nums text-emerald-600">
                            {prize.remaining_quantity}
                          </div>
                          <div className="text-xs text-muted-foreground">Kalan</div>
                        </div>
                        <div>
                          <div className="font-bold tabular-nums text-sky-600">{given}</div>
                          <div className="text-xs text-muted-foreground">Verilen</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={prize.is_active}
                            onCheckedChange={() => void toggleActive(prize)}
                          />
                          <span className="text-sm">{prize.is_active ? 'Aktif' : 'Pasif'}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(prize)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => void handleDelete(prize)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {prizes.length === 0 && (
                <Card className="sm:col-span-2 lg:col-span-3">
                  <CardContent className="py-12 text-center text-muted-foreground">
                    Henüz ödül eklenmedi. İlk ödülünüzü ekleyerek başlayın.
                  </CardContent>
                </Card>
              )}
            </div>

            <Card>
              <CardHeader className="space-y-3">
                <CardTitle>Kazananlar</CardTitle>
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={winSearch}
                    onChange={(e) => setWinSearch(e.target.value)}
                    placeholder="İsim, ödül veya personel ara..."
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">Tarih</th>
                        <th className="py-2 pr-3 font-medium">Müşteri</th>
                        <th className="py-2 pr-3 font-medium">Ödül</th>
                        <th className="py-2 pr-3 font-medium">Personel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWins.map((win) => (
                        <tr key={win.id} className="border-b border-border/50">
                          <td className="py-3 pr-3 whitespace-nowrap">
                            {format(new Date(win.created_at), 'dd MMM yyyy HH:mm', { locale: tr })}
                          </td>
                          <td className="py-3 pr-3">
                            {win.customer_first_name} {win.customer_last_name}
                          </td>
                          <td className="py-3 pr-3">
                            <span className="inline-flex items-center gap-1">
                              {win.prize?.name ?? '—'}
                              {win.is_big_prize && (
                                <Badge variant="secondary" className="text-[10px]">
                                  Büyük
                                </Badge>
                              )}
                            </span>
                          </td>
                          <td className="py-3 pr-3">{win.staff?.full_name ?? '—'}</td>
                        </tr>
                      ))}
                      {filteredWins.length === 0 && (
                        <tr>
                          <td colSpan={4} className="py-8 text-center text-muted-foreground">
                            Kayıt bulunamadı.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Ödülü Düzenle' : 'Yeni Ödül'}</DialogTitle>
            <DialogDescription>
              Ödül adı, adet, büyük ödül işareti ve görselini ayarlayın.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="prize-name">Ödül adı</Label>
              <Input
                id="prize-name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prize-qty">Toplam adet</Label>
              <Input
                id="prize-qty"
                type="number"
                min={0}
                value={form.total_quantity}
                onChange={(e) => setForm((prev) => ({ ...prev, total_quantity: e.target.value }))}
                required
              />
              {editing && (
                <p className="text-xs text-muted-foreground">
                  Verilmiş: {editing.total_quantity - editing.remaining_quantity}. Toplam bundan az
                  olamaz.
                </p>
              )}
            </div>
            <div className="flex items-center justify-between rounded-xl border p-3">
              <div>
                <Label htmlFor="prize-big">Büyük ödül</Label>
                <p className="text-xs text-muted-foreground">Kazanıldığında özel animasyon gösterilir</p>
              </div>
              <Switch
                id="prize-big"
                checked={form.is_big_prize}
                onCheckedChange={(checked) =>
                  setForm((prev) => ({ ...prev, is_big_prize: checked }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Görsel</Label>
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 rounded-xl overflow-hidden bg-muted flex items-center justify-center shrink-0">
                  {form.previewUrl ? (
                    <img src={form.previewUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Gift className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <label className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium cursor-pointer hover:bg-accent hover:text-accent-foreground">
                  <Upload className="h-4 w-4" />
                  Yükle
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={onFileChange}
                  />
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Kaydet
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </CarouselShell>
  );
}
